/**
 * ZOE → Briefs
 *
 * The organisation's recurring briefs: what each one gathers, when it runs,
 * preview, run now, pause, and past runs. Briefs are built by asking Zoe
 * (docs/architecture/zoe_curated_briefs.md §12), not on this page; only the
 * name and schedule can be edited here. Backed by zeecore `/briefs`.
 *
 * Everyone in the organisation may open this page and read every brief (§9
 * D3). Editing, previewing, running, pausing and deleting need the Cognito-managed
 * `zoe-briefs` permission (or admin): without it the controls are not shown,
 * and — the check that actually protects anything — zeecore refuses them.
 *
 * What a section shows follows the server's evidence contract: a failed or
 * skipped section says why and never renders as zero, limitations are always
 * visible, and every figure can be traced to the events behind it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CalendarClock, ChevronDown, ChevronRight, History, Loader2, Pause, Pencil, Play, RefreshCw, Trash2, X,
} from 'lucide-react';
import { briefsApi, BriefsApiError } from '../services/briefsApi';
import { hasPageAccess } from '../utils/pageAccess';
import type {
  AlertState, BriefDefinition, BriefProposal, BriefRecipients, BriefResultItem, BriefRun, BriefRunSummary, BriefSectionResult, BriefSubscriptions, NotifyChannel,
} from '../types/briefs';
import {
  BRIEFS_PERMISSION, STATUS_LABELS, alertRunBadge, dataKinds, alertRunText, alertSummary, authorshipText, nextRunText, runExplanation,
  evidenceText, formatFigure, intervalText, noticeText, scheduleSummary, sectionExplanation, statusTone, type Tone,
} from '../utils/briefPresentation';
import { formFromSchedule, inputOf, scheduleFromForm, type RepeatChoice, type ScheduleForm } from '../utils/briefEdit';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { getUnsavedChangesWarning } from '../utils/unsavedChangesGuard';
import BriefProposalCard from '../components/zoeAssistant/BriefProposalCard';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '../components/ui/badge';

/** Guards for edits on this page share this prefix, so switching brief checks only them. */
const PAGE_GUARD = 'brief-page:';

/** Section/run status → Badge tone (the `Tone` vocabulary maps one-to-one). */
const STATUS_TONE: Record<Tone, BadgeTone> = {
  good: 'success',
  warn: 'warning',
  bad: 'destructive',
  muted: 'neutral',
};

function Header() {
  return (
    <header>
      <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
        <CalendarClock className="w-7 h-7 text-chart-10" />
        Briefs
      </h1>
      <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
        Your organisation&apos;s recurring briefs, built by asking Zoe. Everyone in the organisation can see them;
        creating, running and changing them needs the <code className="text-muted-foreground">{BRIEFS_PERMISSION}</code> permission.
        Every figure links back to the data it came from.
      </p>
    </header>
  );
}

function NoAccess() {
  return (
    <div role="alert" className="rounded-lg border border-warning/40 bg-warning/20 px-4 py-3 text-sm text-warning">
      You don&apos;t have access to Briefs for this organisation.
    </div>
  );
}

export default function ZoeBriefs() {
  // Everyone in the organisation reads briefs (§9 D3). Changing them needs the
  // permission — this only decides which controls to show; zeecore enforces it.
  return <BriefsWorkspace canManage={hasPageAccess(BRIEFS_PERMISSION)} />;
}

function BriefsWorkspace({ canManage }: { canManage: boolean }) {
  const [briefs, setBriefs] = useState<BriefDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<BriefProposal[]>([]);

  // Zoe drafts never created: easy to miss in the chat, so they are shown again here.
  const loadDrafts = useCallback(async () => {
    if (!canManage) return;
    try {
      setDrafts(await briefsApi.listDrafts());
    } catch {
      /* the page works without them */
    }
  }, [canManage]);

  useEffect(() => {
    void loadDrafts();
  }, [loadDrafts]);

  const select = (briefId: string) => {
    if (briefId === selectedId) return;
    const warning = getUnsavedChangesWarning(PAGE_GUARD);
    if (warning && !window.confirm(warning)) return;
    setSelectedId(briefId);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const b = await briefsApi.list();
      setBriefs(b);
      setSelectedId((cur) => (cur && b.some((x) => x.brief_id === cur) ? cur : b[0]?.brief_id ?? null));
    } catch (err) {
      if (err instanceof BriefsApiError && err.status === 403) setForbidden(true);
      else if (err instanceof BriefsApiError && err.status === 404) setUnavailable(true);
      else setLoadError(err instanceof Error ? err.message : 'Could not load briefs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = briefs.find((b) => b.brief_id === selectedId) ?? null;

  const onChanged = (b: BriefDefinition) => setBriefs((prev) => prev.map((x) => (x.brief_id === b.brief_id ? b : x)));
  const onDeleted = (briefId: string) => {
    setBriefs((prev) => prev.filter((x) => x.brief_id !== briefId));
    setSelectedId((cur) => (cur === briefId ? null : cur));
  };

  return (
    <div className="space-y-5 w-full max-w-6xl">
      <Header />
      {forbidden ? (
        <NoAccess />
      ) : unavailable ? (
        <div className="rounded-lg border border-border bg-card/80 px-4 py-3 text-sm text-muted-foreground">
          Briefs aren&apos;t switched on for this environment yet.
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading briefs…
        </div>
      ) : loadError ? (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/20 px-4 py-3 text-sm text-destructive">
          <span>{loadError}</span>
          <Button type="button" variant="link" size="sm" onClick={() => void load()} className="gap-1 text-destructive">
            <RefreshCw className="w-4 h-4" aria-hidden /> Retry
          </Button>
        </div>
      ) : (
        <>
        {drafts.length > 0 && (
          <section aria-label="Not created yet" className="space-y-2">
            <h2 className="flex items-center gap-1.5 text-sm font-medium text-warning">
              <AlertTriangle className="w-4 h-4" /> Not created yet
            </h2>
            <p className="text-xs text-muted-foreground">
              Zoe drafted {drafts.length === 1 ? 'this' : 'these'} for you, but {drafts.length === 1 ? 'it was' : 'they were'} never
              created, so nothing runs or is sent. Create or cancel {drafts.length === 1 ? 'it' : 'each one'} here. Drafts are kept for 24 hours.
            </p>
            {drafts.map((d) => (
              <BriefProposalCard
                key={`${d.draft_id}:${d.revision}`}
                proposal={d}
                onCreated={(b) => {
                  setDrafts((prev) => prev.filter((x) => x.draft_id !== d.draft_id));
                  setBriefs((prev) => (prev.some((x) => x.brief_id === b.brief_id) ? prev : [...prev, b]));
                  setSelectedId(b.brief_id);
                }}
                onCancelled={() => setDrafts((prev) => prev.filter((x) => x.draft_id !== d.draft_id))}
              />
            ))}
          </section>
        )}
        <div className="grid gap-4 md:grid-cols-16rem-1fr">
          <aside className="space-y-2">
            <p className="text-xs text-muted-foreground px-1">
              {canManage
                ? 'To create a brief, ask Zoe: say what you want to see and when, for example \u201cevery weekday at 7am, show me open defects and yesterday\u2019s late deliveries\u201d.'
                : `You can view these briefs. Creating or changing them needs the ${BRIEFS_PERMISSION} permission.`}
            </p>
            {briefs.length === 0 ? (
              <p className="text-sm text-muted-foreground px-1">No briefs yet.</p>
            ) : (
              <ul className="space-y-1">
                {briefs.map((b) => (
                  <li key={b.brief_id}>
                    <Button
                      type="button"
                      variant="outline"
                      size="md"
                      onClick={() => select(b.brief_id)}
                      className={`w-full h-auto items-start justify-start px-3 py-2 text-left ${
                        b.brief_id === selectedId
                          ? 'border-ring bg-ring/30'
                          : 'border-border bg-card/60 hover:border-accent'
                      }`}
                    >
                      <span className="flex items-center gap-1.5 text-sm text-foreground">
                        <span className="truncate">{b.name}</span>
                        {b.condition && <Badge tone="warning" className="shrink-0 px-1.5 uppercase tracking-wide">Alert</Badge>}
                      </span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {b.enabled ? scheduleSummary(b.schedule) : 'Paused'}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section className="min-w-0">
            {selected ? (
              <BriefDetail key={selected.brief_id} brief={selected} canManage={canManage} onChanged={onChanged} onDeleted={onDeleted} />
            ) : (
              <p className="text-sm text-muted-foreground">Select a brief.</p>
            )}
          </section>
        </div>
        </>
      )}
    </div>
  );
}

function BriefDetail(props: {
  brief: BriefDefinition;
  /** Show the controls that change or run the brief. The server enforces the same rule. */
  canManage: boolean;
  onChanged: (brief: BriefDefinition) => void;
  onDeleted: (briefId: string) => void;
}) {
  const { brief } = props;
  const [history, setHistory] = useState<BriefRunSummary[] | null>(null);
  const [run, setRun] = useState<{ run: BriefRun; saved: boolean } | null>(null);
  const [busy, setBusy] = useState<'preview' | 'run' | 'pause' | 'delete' | 'open' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alertState, setAlertState] = useState<AlertState | null>(null);
  const [dataTriggers, setDataTriggers] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const isAlert = !!brief.condition;

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await briefsApi.runs(brief.brief_id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load run history');
    }
  }, [brief.brief_id]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // An alert says when it has been unable to check (several unreadable runs in a row).
  useEffect(() => {
    if (!isAlert) return;
    let live = true;
    briefsApi.get(brief.brief_id).then((r) => {
      if (!live) return;
      setAlertState(r.alertState);
      setDataTriggers(r.dataTriggers ?? []);
    }).catch(() => { /* the page still works without it */ });
    return () => { live = false; };
  }, [brief.brief_id, isAlert]);

  const act = async (kind: 'preview' | 'run' | 'pause' | 'delete' | 'open', fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card/80 p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-foreground">{brief.name}</h2>
            <p className="text-sm text-muted-foreground">
              {brief.enabled ? scheduleSummary(brief.schedule) : 'Paused'}
              {nextRunText(brief) && <span className="text-muted-foreground"> · {nextRunText(brief)}</span>}
            </p>
            <p className="text-xs text-muted-foreground">{authorshipText(brief)}</p>
          </div>
          {props.canManage && <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="md" disabled={busy !== null || editing} onClick={() => setEditing(true)}
              className="border-accent text-foreground hover:border-muted-foreground">
              <Pencil className="w-4 h-4" aria-hidden /> Edit
            </Button>
            <Button type="button" variant="outline" size="md" disabled={busy !== null} loading={busy === 'preview'} onClick={() => void act('preview', async () => setRun({ run: await briefsApi.preview(brief.brief_id), saved: false }))}
              title="See it now. Nothing is saved or sent."
              className="border-ring text-ring hover:bg-ring/40">
              <Play className="w-4 h-4" aria-hidden /> Preview
            </Button>
            <Button type="button" variant="default" size="md" disabled={busy !== null} loading={busy === 'run'} onClick={() => void act('run', async () => {
              setRun({ run: await briefsApi.run(brief.brief_id), saved: true });
              await loadHistory();
            })} title={isAlert ? 'Run it and save the result. Alerts only send on their schedule.' : 'Run it, save the result, and email it to everyone subscribed.'}
              className="bg-ring font-medium text-white hover:bg-ring">
              <Play className="w-4 h-4" aria-hidden /> Run now
            </Button>
            <Button type="button" variant="outline" size="md" disabled={busy !== null} loading={busy === 'pause'} onClick={() => void act('pause', async () => {
              // The whole brief goes back unchanged but for `enabled`, under the revision shown: a brief changed since is a conflict.
              props.onChanged(await briefsApi.update(brief.brief_id, brief.revision, inputOf(brief, { enabled: !brief.enabled })));
            })} className="border-accent text-foreground hover:border-muted-foreground">
              {brief.enabled ? <Pause className="w-4 h-4" aria-hidden /> : <Play className="w-4 h-4" aria-hidden />}
              {brief.enabled ? 'Pause' : 'Resume'}
            </Button>
            <Button type="button" variant="destructive" size="md" disabled={busy !== null} onClick={() => {
              if (!window.confirm(`Delete “${brief.name}” and its run history? This can't be undone.`)) return;
              void act('delete', async () => { await briefsApi.remove(brief.brief_id); props.onDeleted(brief.brief_id); });
            }} className="border border-destructive/60 text-destructive hover:bg-destructive/30">
              <Trash2 className="w-4 h-4" aria-hidden /> Delete
            </Button>
          </div>}
        </div>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{brief.plan.question_understanding}</p>
          {isAlert && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning space-y-1">
              <p>{alertSummary(brief)}</p>
              {dataTriggers.length > 0 && (
                <p className="text-xs text-warning/80">
                  {brief.schedule.frequency === 'on_data'
                    ? `Checks a few minutes after new ${dataKinds(dataTriggers)} data arrives, and at no other time. It won't notice if that data stops arriving: Data Import's feed health does.`
                    : `Also checks a few minutes after new ${dataKinds(dataTriggers)} data arrives, as well as on its schedule.`}
                </p>
              )}
              <p className="text-xs text-warning/80">
                Previews and Run now show what would be sent without changing what was last sent.
              </p>
              {alertState?.cannot_check && (
                <p role="alert" className="flex items-start gap-1.5 text-xs text-destructive">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  This alert hasn&apos;t been able to check its data for the last {alertState.unreadable_streak} runs, so it can&apos;t send. Nothing is assumed while it can&apos;t check.
                </p>
              )}
            </div>
          )}
          <ul className="flex flex-wrap gap-2">
            {brief.template.sections.map((s) => (
              <li key={s.section_id}><Badge tone="neutral" className="px-2.5">{s.heading}</Badge></li>
            ))}
          </ul>
          {props.canManage && (
            <p className="text-xs text-muted-foreground">
              Built with Zoe. You can edit its name and schedule here; to change what it {isAlert ? 'checks or sends' : 'reports'}, ask Zoe for a new {isAlert ? 'alert' : 'brief'} and delete this one.
            </p>
          )}
        </div>
        {editing && (
          <EditBrief brief={brief} isAlert={isAlert} canCheckOnData={dataTriggers.length > 0}
            onSaved={(b) => { props.onChanged(b); setEditing(false); }} onCancel={() => setEditing(false)} />
        )}
      </div>

      <NotifyMe briefId={brief.brief_id} isAlert={isAlert} />
      {props.canManage && <SendTo briefId={brief.brief_id} briefName={brief.name} />}

      {error && <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/20 px-3 py-2 text-sm text-destructive">{error}</div>}

      {run && <RunView run={run.run} saved={run.saved} timezone={brief.schedule.timezone} onClose={() => setRun(null)} />}

      <div className="rounded-lg border border-border bg-card/80 p-4">
        <h3 className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-2">
          <History className="w-4 h-4" /> Run history
        </h3>
        {history === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {props.canManage ? 'No saved runs yet. Previews aren\u2019t saved; use Run now to keep one.' : 'No saved runs yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {history.map((h) => (
              <li key={h.run_id} className="flex items-center justify-between gap-3 py-1.5">
                <Button type="button" variant="link" size="sm" disabled={busy !== null} onClick={() => void act('open', async () => setRun({ run: await briefsApi.getRun(brief.brief_id, h.run_id), saved: true }))}
                  className="no-underline px-0 text-left text-ring hover:underline">
                  {new Date(h.trigger === 'scheduled' && h.scheduled_at ? h.scheduled_at : h.executed_at)
                    .toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: brief.schedule.timezone })}
                </Button>
                {h.trigger === 'scheduled'
                  ? <span className="text-xs text-muted-foreground">scheduled</span>
                  : h.triggered_by_label && <span className="text-xs text-muted-foreground truncate">run by {h.triggered_by_label}</span>}
                {h.alert && (
                  <span
                    title={h.alert.message}
                    className={`text-xs ${h.trigger === 'scheduled' && h.alert.sent ? 'text-warning' : h.alert.reason === 'unreadable' ? 'text-destructive' : 'text-muted-foreground'}`}
                  >
                    {alertRunBadge(h.alert, h.trigger)}
                  </span>
                )}
                <StatusBadge status={h.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const FREQUENCY_CHOICES: Array<{ value: ScheduleForm['frequency']; label: string }> = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Once a week' },
];
const DAY_CHOICES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const REPEAT_CHOICES: Array<{ value: RepeatChoice; label: string }> = [
  { value: 0, label: 'Once' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every hour' },
];

/**
 * Name and schedule only. Everything else goes back as loaded, under the
 * revision shown, so a brief changed since is refused (409) rather than
 * overwritten. The server decides whether the schedule is allowed.
 */
function EditBrief(props: {
  brief: BriefDefinition;
  isAlert: boolean;
  /** The alert reads data whose arrival is announced, so it can check only when that data arrives. */
  canCheckOnData: boolean;
  onSaved: (brief: BriefDefinition) => void;
  onCancel: () => void;
}) {
  const { brief, isAlert } = props;
  const [name, setName] = useState(brief.name);
  const [form, setForm] = useState<ScheduleForm>(() => formFromSchedule(brief.schedule));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (change: Partial<ScheduleForm>) => setForm((f) => ({ ...f, ...change }));
  const [initial] = useState(() => JSON.stringify({ name: brief.name, form: formFromSchedule(brief.schedule) }));
  const dirty = JSON.stringify({ name, form }) !== initial;
  useUnsavedChanges(
    `${PAGE_GUARD}edit:${brief.brief_id}`,
    dirty ? `Your changes to "${brief.name}" haven't been saved. Leave without saving them?` : null,
  );

  const frequencies = isAlert && (props.canCheckOnData || brief.schedule.frequency === 'on_data')
    ? [...FREQUENCY_CHOICES, { value: 'on_data' as const, label: 'When new data arrives' }]
    : FREQUENCY_CHOICES;
  const clock = form.frequency !== 'on_data';
  const repeating = isAlert && clock && form.every_minutes !== 0;
  const missing = !name.trim() ? 'Give it a name.' : clock && !form.local_time ? 'Choose a time.' : null;

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const schedule = scheduleFromForm(form, brief.schedule.timezone, isAlert);
      props.onSaved(await briefsApi.update(brief.brief_id, brief.revision, inputOf(brief, { name: name.trim(), schedule })));
    } catch (e) {
      setErr(e instanceof BriefsApiError && e.status === 409
        ? 'Someone changed this since you opened it. Reload the page to see their change, then edit again.'
        : e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const field = 'rounded-md border border-accent bg-background px-2 py-1.5 text-sm text-foreground';
  const label = 'flex flex-col gap-1 text-xs text-muted-foreground';
  return (
    <form aria-label="Edit" className="space-y-3 border-t border-border pt-3" onSubmit={(e) => { e.preventDefault(); if (!missing) void save(); }}>
      <label className={label}>
        Name
        <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} className={field} />
      </label>
      <div className="flex flex-wrap gap-3">
        <label className={label}>
          {isAlert ? 'Checks' : 'Runs'}
          <select value={form.frequency} onChange={(e) => set({ frequency: e.target.value as ScheduleForm['frequency'] })} className={field}>
            {frequencies.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
        {form.frequency === 'weekly' && (
          <label className={label}>
            Day
            <select value={form.day_of_week} onChange={(e) => set({ day_of_week: Number(e.target.value) })} className={field}>
              {DAY_CHOICES.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </label>
        )}
        {clock && (
          <label className={label}>
            {repeating ? 'From' : 'At'}
            <input type="time" value={form.local_time} onChange={(e) => set({ local_time: e.target.value })} className={field} />
          </label>
        )}
        {isAlert && clock && (
          <label className={label}>
            Repeat
            <select value={form.every_minutes} onChange={(e) => set({ every_minutes: Number(e.target.value) as RepeatChoice })} className={field}>
              {REPEAT_CHOICES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
        )}
        {repeating && (
          <label className={label}>
            Until (blank: end of day)
            <input type="time" value={form.until} onChange={(e) => set({ until: e.target.value })} className={field} />
          </label>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {clock
          ? `Times are in ${brief.schedule.timezone}.`
          : 'Checks a few minutes after new data it reads arrives, and at no other time.'}
      </p>
      {err && <p role="alert" className="text-xs text-destructive">{err}</p>}
      <div className="flex gap-2">
        <Button type="submit" variant="default" size="md" disabled={!!missing} loading={saving} title={missing ?? undefined}
          className="bg-ring font-medium text-white hover:bg-ring">
          Save
        </Button>
        <Button type="button" variant="outline" size="md" disabled={saving} onClick={props.onCancel}
          className="border-accent text-foreground hover:border-muted-foreground">
          Cancel
        </Button>
        {dirty && !saving && (
          <span role="status" className="inline-flex items-center gap-1 text-xs font-medium text-amber-200">
            <AlertTriangle className="w-3.5 h-3.5" /> Unsaved changes: press Save to keep them.
          </span>
        )}
      </div>
    </form>
  );
}

const CHANNEL_LABEL: Record<NotifyChannel, string> = { email: 'Email', sms: 'Text message' };

/**
 * "Notify me": anyone who can read a brief may subscribe themselves (R6-4).
 * Addresses come from the user's account, so there is nothing to type. Hidden
 * when notifications are not switched on for this environment.
 */
function NotifyMe({ briefId, isAlert }: { briefId: string; isAlert: boolean }) {
  const [subs, setSubs] = useState<BriefSubscriptions | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    briefsApi.subscriptions(briefId).then((s) => { if (live) setSubs(s); }).catch(() => { /* not switched on: nothing to show */ });
    return () => { live = false; };
  }, [briefId]);
  if (!subs) return null;

  const toggle = async (c: NotifyChannel) => {
    const next = subs.mine.includes(c) ? subs.mine.filter((x) => x !== c) : [...subs.mine, c];
    setSaving(true);
    setErr(null);
    try {
      const mine = await briefsApi.setMySubscription(briefId, next);
      setSubs({ ...subs, mine, subscribers: subs.subscribers + (mine.length > 0 ? 1 : 0) - (subs.mine.length > 0 ? 1 : 0) });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card/80 p-4 space-y-2" aria-label="Notify me">
      <h3 className="text-sm font-medium text-foreground">Notify me</h3>
      <p className="text-xs text-muted-foreground">
        {isAlert ? 'Sent when this alert fires.' : 'Sent after each scheduled run.'} To the address on your account.
        {subs.subscribers > 0 && ` ${subs.subscribers} ${subs.subscribers === 1 ? 'person is' : 'people are'} subscribed.`}
        {(subs.addresses ?? 0) > 0 && ` Also sent to ${subs.addresses} ${subs.addresses === 1 ? 'address' : 'addresses'}.`}
      </p>
      <div className="flex flex-wrap gap-4">
        {subs.channels_known.map((c) => {
          const available = subs.channels_available.includes(c);
          return (
            <label key={c} className={`inline-flex items-center gap-1.5 text-sm ${available ? 'text-foreground' : 'text-muted-foreground'}`}>
              <input
                type="checkbox"
                checked={subs.mine.includes(c)}
                disabled={saving || (!available && !subs.mine.includes(c))}
                onChange={() => void toggle(c)}
              />
              {CHANNEL_LABEL[c]}
              {!available && <span className="text-xs">(not available yet)</span>}
            </label>
          );
        })}
      </div>
      {err && <p role="alert" className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

/** Addresses the brief is also emailed to — typically a group mailbox such as ops@… (managers only). */
function SendTo({ briefId, briefName }: { briefId: string; briefName: string }) {
  const [saved, setSaved] = useState<BriefRecipients | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    briefsApi.recipients(briefId).then((r) => {
      if (!live) return;
      setSaved(r);
      setDraft(r.addresses.join('\n'));
    }).catch(() => { /* not switched on: nothing to show */ });
    return () => { live = false; };
  }, [briefId]);
  const wanted = draft.split(/[\s,;]+/).map((a) => a.trim()).filter(Boolean);
  const changed = !!saved && wanted.join('\n').toLowerCase() !== saved.addresses.join('\n');
  useUnsavedChanges(
    `${PAGE_GUARD}recipients:${briefId}`,
    changed ? `The email addresses for "${briefName}" haven't been saved, so nobody new will get it. Leave without saving them?` : null,
  );
  if (!saved) return null;

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await briefsApi.setRecipients(briefId, wanted);
      setSaved(r);
      setDraft(r.addresses.join('\n'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card/80 p-4 space-y-2" aria-label="Also send to">
      <h3 className="text-sm font-medium text-foreground">Also send to</h3>
      <p className="text-xs text-muted-foreground">
        Email addresses that receive every message, such as a team mailbox. One per line, up to {saved.max}. Only people who can manage briefs see these.
      </p>
      <textarea
        aria-label="Email addresses"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={Math.min(Math.max(saved.addresses.length + 1, 2), 6)}
        placeholder="ops@example.com"
        className="w-full rounded-md border border-accent bg-background px-2 py-1.5 text-sm text-foreground"
      />
      <div className="flex items-center gap-2">
        <Button type="button" variant={changed ? 'default' : 'outline'} size="md" disabled={saving || !changed} loading={saving} onClick={() => void save()}
          className={changed ? 'bg-ring font-medium text-white hover:bg-ring' : 'border border-accent text-foreground hover:border-muted-foreground'}>
          Save addresses
        </Button>
        {changed && !saving && (
          <span role="status" className="inline-flex items-center gap-1 text-xs font-medium text-amber-200">
            <AlertTriangle className="w-3.5 h-3.5" /> Not saved yet: press Save addresses.
          </span>
        )}
      </div>
      {err && <p role="alert" className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: BriefSectionResult['status'] }) {
  return (
    <Badge tone={STATUS_TONE[statusTone(status)]}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

export function RunView(props: { run: BriefRun; saved: boolean; timezone?: string; onClose?: () => void }) {
  const { run } = props;
  return (
    <div className="rounded-lg border border-border bg-card/80 p-4 space-y-4" aria-label="Brief result">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusBadge status={run.status} />
          <span className="text-sm text-muted-foreground">
            {run.trigger === 'scheduled' ? 'Scheduled run for' : props.saved ? 'Run' : 'Preview'} ·{' '}
            {new Date(run.trigger === 'scheduled' && run.scheduled_at ? run.scheduled_at : run.executed_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', ...(props.timezone ? { timeZone: props.timezone } : {}) })}
          </span>
          {!props.saved && <span className="text-xs text-muted-foreground">Not saved</span>}
        </div>
        {props.onClose && (
          <Button type="button" variant="ghost" size="iconSm" aria-label="Close result" onClick={props.onClose}>
            <X className="w-4 h-4" aria-hidden />
          </Button>
        )}
      </div>
      {runExplanation(run, props.timezone) && (
        <p className="text-sm text-warning">{runExplanation(run, props.timezone)}</p>
      )}
      {run.alert && <AlertResult run={run} />}
      {run.sections.map((s) => <SectionView key={s.section_id} section={s} timezone={props.timezone} />)}
    </div>
  );
}

/** What an alert run decided, and its one line. The sections below show the data it checked. */
function AlertResult({ run }: { run: BriefRun }) {
  const a = run.alert!;
  const sentNow = run.trigger === 'scheduled' && a.sent;
  return (
    <div
      aria-label="Alert"
      className={`rounded-md border px-3 py-2 space-y-1 ${sentNow ? 'border-warning/60 bg-warning/20' : 'border-border bg-background/40'}`}
    >
      <p className={`text-sm ${a.condition.reason === 'unreadable' ? 'text-destructive' : 'text-foreground'}`}>{alertRunText(run)}</p>
      {a.message && <p className="text-sm font-medium text-foreground">“{a.message.text}”</p>}
      {a.condition.unavailable && <p className="text-xs text-muted-foreground">{a.condition.unavailable.message}</p>}
      {a.message_unavailable && <p className="text-xs text-muted-foreground">The one-line message couldn&apos;t be written: {a.message_unavailable}</p>}
    </div>
  );
}

function SectionView({ section: s, timezone }: { section: BriefSectionResult; timezone?: string }) {
  const explanation = sectionExplanation(s);
  const figures = s.items.filter((i): i is Extract<BriefResultItem, { kind: 'figure' }> => i.kind === 'figure');
  const rest = s.items.filter((i) => i.kind !== 'figure');
  return (
    <section className="space-y-2 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-foreground">{s.label}</h4>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{intervalText(s, timezone)}</span>
          <StatusBadge status={s.status} />
        </div>
      </div>
      {explanation && <p className="text-sm text-muted-foreground">{explanation}</p>}
      {s.window_deviation && (
        <p className="text-xs text-warning">
          Covers {s.window_deviation.actual}, not the period you chose: {s.window_deviation.reason}
        </p>
      )}
      {figures.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-3">
          {figures.map((f) => (
            <div key={f.item_id} className="rounded-md border border-border bg-background/60 px-3 py-2">
              <div className="text-2xl font-semibold text-foreground">{formatFigure(f)}</div>
              <div className="text-sm text-foreground">{f.title}</div>
              <div className="text-xs text-muted-foreground">{f.summary}</div>
              <Evidence item={f} />
            </div>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <ul className="space-y-1.5">
          {rest.map((i) => (
            <li key={i.item_id} className="rounded-md border border-border bg-background/40 px-3 py-2">
              {i.kind === 'notice' ? (
                <span className="text-sm text-muted-foreground">{noticeText(i.code)}</span>
              ) : (
                <>
                  <div className="text-sm text-foreground">{i.title}</div>
                  <div className="text-xs text-muted-foreground">{i.summary}</div>
                  <Evidence item={i} />
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {s.limitations.length > 0 && (
        <ul className="space-y-1" aria-label="Limitations">
          {s.limitations.map((l, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {l.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Evidence({ item }: { item: BriefResultItem }) {
  const [open, setOpen] = useState(false);
  const text = evidenceText(item);
  const refs = useMemo(() => (item.kind === 'notice' ? [] : item.evidence_refs), [item]);
  if (!text) return null;
  if (refs.length === 0) return <div className="mt-1 text-xs text-muted-foreground">{text}</div>;
  return (
    <div className="mt-1">
      <Button type="button" variant="link" size="sm" onClick={() => setOpen((o) => !o)} className="no-underline gap-0.5 px-0 text-ring hover:underline">
        {open ? <ChevronDown className="w-3 h-3" aria-hidden /> : <ChevronRight className="w-3 h-3" aria-hidden />} {text}
      </Button>
      {open && (
        <ul className="mt-1 max-h-40 overflow-auto font-mono text-xs text-muted-foreground">
          {refs.map((r) => <li key={`${r.event_type}:${r.event_id}`}>{r.event_type} · {r.event_id}</li>)}
        </ul>
      )}
    </div>
  );
}
